import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline'

// Components
import { AuthGradient } from '../../components/AuthGradient'
import { Button } from '../../components/Button'

// Images
import FetchLogo from '../../assets/images/fetch_text_logo.svg'
import FetchDog from '../../assets/images/fetch_dog.png'
import EventstackLogo from '../../assets/images/eventstack_logo.svg'

// App Store URLs
const APP_STORE_URL = 'https://itunes.apple.com/us/app/fetch-leads/id6472296338'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.eventstack.fetch'

/**
 * FetchAppLanding
 *
 * A public landing page for Fetch app downloads and deep linking.
 * Handles email and licenseId URL params to pre-fill the app login.
 * Displays app store links and attempts to auto-launch the app.
 */
const FetchAppLanding = () => {
  const [searchParams] = useSearchParams()
  const [hasAttemptedLaunch, setHasAttemptedLaunch] = useState(false)

  // Extract URL params
  const email = searchParams.get('email') || ''
  const licenseId = searchParams.get('licenseId') || ''

  // Build the deep link URL
  const deepLinkScheme = import.meta.env.VITE_FETCH_DEEP_LINK_SCHEME || 'fetch'
  const deepLinkParams = new URLSearchParams()
  if (email) deepLinkParams.set('email', email)
  if (licenseId) deepLinkParams.set('licenseId', licenseId)
  const deepLinkQueryString = deepLinkParams.toString()
  const deepLinkUrl = deepLinkQueryString
    ? `${deepLinkScheme}://login?${deepLinkQueryString}`
    : `${deepLinkScheme}://login`

  // Attempt to launch the app via deep link
  const handleLaunchApp = () => {
    window.location.href = deepLinkUrl
  }

  // Auto-attempt to launch the app on page load
  useEffect(() => {
    if (!hasAttemptedLaunch && (email || licenseId)) {
      setHasAttemptedLaunch(true)
      // Small delay to ensure the page has rendered
      const timer = setTimeout(() => {
        handleLaunchApp()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [hasAttemptedLaunch, email, licenseId])

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#181823]">
      <AuthGradient />
      <div className="relative z-10 flex h-full flex-col overflow-y-auto text-white">
        {/* Header */}
        <div className="flex items-center justify-center px-6 pt-4 sm:pt-8">
          <img src={FetchLogo} alt="Fetch" className="h-10 sm:h-12" />
        </div>

        {/* Main Content */}
        <div className="flex flex-col items-center px-6 py-6">
          {/* Hero Image */}
          <div className="mb-4 sm:mb-8">
            <img
              src={FetchDog}
              alt="Fetch App"
              className="max-h-[42vh] w-auto rounded-2xl object-cover shadow-lg"
            />
          </div>

          {/* Headline */}
          <h1 className="mb-2 text-center text-xl font-bold sm:mb-3 sm:text-3xl">
            Your new best friend in lead retrieval!
          </h1>
          <p className="mb-4 max-w-md text-center text-sm text-gray-400 sm:mb-8 sm:text-base">
            Download the Fetch app to start capturing leads at your event.
          </p>

          {/* Buttons */}
          <div className="flex w-full max-w-sm flex-col gap-4">
            {/* Open in App */}
            <div className="flex flex-col items-center gap-2 sm:gap-3">
              <span className="text-xs text-gray-400 sm:text-sm">
                Already have Fetch installed?
              </span>
              <Button
                label="Open in App"
                icon={<DevicePhoneMobileIcon className="h-5 w-5" />}
                onClick={handleLaunchApp}
                size="lg"
                fullWidth
                background="bg-purple"
              />
            </div>

            {/* App Store Buttons */}
            <div className="mt-2 flex flex-col gap-3 sm:mt-4 sm:flex-row">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-700"
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight text-gray-400">Download on the</span>
                  <span className="leading-tight">App Store</span>
                </div>
              </a>

              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-700"
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight text-gray-400">Get it on</span>
                  <span className="leading-tight">Google Play</span>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-center gap-x-2 px-6 pb-4 pt-4 sm:gap-x-3 sm:pb-8 sm:pt-8">
          <span className="text-sm font-bold sm:text-base">Powered by</span>
          <img src={EventstackLogo} alt="Eventstack" className="h-6 sm:h-8" />
        </div>
      </div>
    </div>
  )
}

export default FetchAppLanding
